import { useState, useCallback, useRef } from 'react';
import { API_URL } from '../lib/env';

export type SurveyTrigger = 'place_lookup' | 'navigation';

interface SurveyState {
    trigger: SurveyTrigger;
    placeName?: string;
}

const SESSION_KEY = 'coordal-survey-shown';
const LAST_SHOWN_KEY = 'coordal-survey-last-shown';
const COOLDOWN_DAYS = 7;
const COOLDOWN_MS = COOLDOWN_DAYS * 24 * 60 * 60 * 1000;

function canShowSurvey(): boolean {
    if (sessionStorage.getItem(SESSION_KEY)) return false;
    const lastShown = localStorage.getItem(LAST_SHOWN_KEY);
    if (lastShown && Date.now() - parseInt(lastShown) < COOLDOWN_MS) return false;
    return true;
}

function markSurveyShown(): void {
    sessionStorage.setItem(SESSION_KEY, '1');
    localStorage.setItem(LAST_SHOWN_KEY, String(Date.now()));
}

export function useFeedbackSurvey() {
    const [survey, setSurvey] = useState<SurveyState | null>(null);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const triggerSurvey = useCallback((trigger: SurveyTrigger, placeName?: string, delayMs = 0) => {
        if (!canShowSurvey()) return;

        if (timerRef.current) clearTimeout(timerRef.current);

        if (delayMs > 0) {
            timerRef.current = setTimeout(() => {
                if (!canShowSurvey()) return;
                markSurveyShown();
                setSurvey({ trigger, placeName });
            }, delayMs);
        } else {
            markSurveyShown();
            setSurvey({ trigger, placeName });
        }
    }, []);

    const cancelPending = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const dismissSurvey = useCallback(() => {
        cancelPending();
        setSurvey(null);
    }, [cancelPending]);

    const submitSurvey = useCallback(async (rating: number, comment: string) => {
        if (!survey) return;
        try {
            await fetch(`${API_URL}/map/feedback`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    trigger: survey.trigger,
                    rating,
                    comment: comment.trim() || null,
                    place_name: survey.placeName || null,
                }),
            });
        } catch {
            // Non-critical — silently ignore network failures
        }
    }, [survey]);

    return { survey, triggerSurvey, cancelPending, dismissSurvey, submitSurvey };
}
